import React, { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { MARK } from '../../../constants';
import {
  acoesDoServico, etapasDoServico, situacaoFinanceira, acoesDoCliente, servicosDoCliente,
  abertosPrimeiro, gruposDePendencias, filaDoGrupo,
} from '../../../utils/assistente/fluxos';
import { reais } from '../../../utils/assistente/pendencias';
import PrenhasPorCliente from './PrenhasPorCliente';

/**
 * OS ELEMENTOS DO ASSISTENTE: lista, cartão, pendências.
 *
 * A primeira versão punha tudo em balão de chat — serviços como botões,
 * ações em grupos recolhíveis — e não ficou bom (29/10/2026). Vários itens
 * pedem uma LISTA de verdade; um item pede um CARTÃO. Os dois leem o estado
 * de agora (`ctx`), não uma cópia do momento da pergunta: depois de faturar,
 * a linha já mostra "faturado".
 *
 * Estado por texto e marca (⚠️ para atrasado), não por cor; cor só no que se
 * clica.
 */

const dataCurta = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '');

const MODELO = {
  por_te: 'por TE', por_prenhez: 'por prenhez', por_embriao: 'por embrião', diaria: 'diária',
  pacote_completo: 'pacote', personalizado: 'personalizado', sem_cobranca: 'sem cobrança',
};

const primario = 'px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm whitespace-nowrap';
const secundario = 'px-2 py-1 rounded-md text-blue-700 underline-offset-2 hover:underline hover:bg-blue-50 text-sm';

/** Onde o serviço está, numa linha: a última feita, o que falta, o atraso. */
const progresso = (s) => {
  const etapas = etapasDoServico(s);
  const feitas = etapas.filter(e => e.feita);
  const faltam = etapas.filter(e => !e.feita);
  const atrasada = faltam.find(e => e.atrasada);
  return [
    feitas.length ? `${feitas[feitas.length - 1].label} feita` : 'nada lançado',
    faltam.length ? `falta ${faltam.map(e => e.label).join(', ')}` : null,
    atrasada ? `${MARK.WARN} ${atrasada.label} era ${dataCurta(atrasada.data)}` : null,
  ].filter(Boolean).join(' · ');
};

/** O cartão de um serviço: cabeçalho, linha do tempo, dinheiro e ações. */
export const CartaoDeServico = ({ s, ctx, onAcao, embutido = false }) => {
  const { principais, secoes } = acoesDoServico(s, ctx);
  return (
    <div className={embutido ? 'px-3 pb-3' : 'bg-white border border-slate-200 rounded-xl p-4'}>
      {!embutido && (
        <div className="mb-3">
          <p className="font-semibold text-slate-900">{s.service_code} · {s.client_name}</p>
          <p className="text-xs text-slate-500">
            {s.date ? new Date(`${String(s.date).slice(0, 10)}T12:00:00`).toLocaleDateString('pt-BR') : ''}
            {MODELO[s.billing_model] ? ` · ${MODELO[s.billing_model]}` : ''}
          </p>
        </div>
      )}

      <ol className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
        {etapasDoServico(s).map(e => (
          <li key={e.id} className={e.feita ? 'text-slate-800' : 'text-slate-500'}>
            <span className="font-medium">{e.label}</span>{' '}
            {e.feita ? `feita ${dataCurta(e.data)}`
              : e.atrasada ? `${MARK.WARN} era ${dataCurta(e.data)}`
              : e.data ? `agendada ${dataCurta(e.data)}` : 'pendente'}
          </li>
        ))}
      </ol>
      <p className="mt-1 text-sm text-slate-600">{situacaoFinanceira(s, ctx)}</p>

      {principais.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {principais.map((b, i) => (
            <button key={i} onClick={() => onAcao(b.acao)} className={primario}>{b.rotulo}</button>
          ))}
        </div>
      )}

      {secoes.length > 0 && (
        <dl className="mt-3 space-y-1">
          {secoes.map(sec => (
            <div key={sec.titulo} className="flex flex-wrap items-baseline gap-x-1">
              <dt className="w-24 shrink-0 text-xs uppercase tracking-wide text-slate-400">{sec.titulo}</dt>
              <dd className="flex flex-wrap gap-x-1">
                {sec.botoes.map((b, i) => (
                  <button key={i} onClick={() => onAcao(b.acao)} className={secundario}>{b.rotulo}</button>
                ))}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
};

/**
 * A lista de serviços. Cada linha diz quem, quando, onde está e o dinheiro;
 * à direita, a ação da linha (a pedida, ou o próximo passo). Clicar na linha
 * abre o cartão ali mesmo — um por vez.
 */
export const ListaDeServicos = ({ ids = [], notas = {}, acoes = {}, ctx, onAcao, max = 30 }) => {
  const [aberto, setAberto] = useState(null);
  const [todos, setTodos] = useState(false);
  const porId = new Map((ctx.services || []).map(s => [s.id, s]));
  const lista = ids.map(id => porId.get(id)).filter(Boolean);
  const visiveis = todos ? lista : lista.slice(0, max);

  return (
    <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100 overflow-hidden">
      {visiveis.map(s => {
        const acao = acoes[s.id] || acoesDoServico(s, ctx).principais[0];
        const nota = notas[s.id];
        return (
          <div key={s.id}>
            <div role="button" tabIndex={0}
              onClick={() => setAberto(aberto === s.id ? null : s.id)}
              onKeyDown={(e) => { if (e.key === 'Enter') setAberto(aberto === s.id ? null : s.id); }}
              className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50">
              <ChevronRight className={`w-4 h-4 shrink-0 text-slate-400 transition-transform ${aberto === s.id ? 'rotate-90' : ''}`} />
              <div className="min-w-0 flex-1 grid gap-x-4 lg:grid-cols-[10rem_minmax(0,1fr)_minmax(0,1.3fr)_minmax(0,1fr)]">
                <p className="text-sm font-medium text-slate-900">
                  {s.service_code} <span className="font-normal text-slate-500">{s.date ? dataCurta(String(s.date).slice(0, 10)) : ''}</span>
                </p>
                <p className="text-sm text-slate-800 truncate">{s.client_name}</p>
                <p className="text-sm text-slate-600 truncate">{nota || progresso(s)}</p>
                <p className="text-sm text-slate-600 truncate">{situacaoFinanceira(s, ctx)}</p>
              </div>
              {acao && (
                <button onClick={(e) => { e.stopPropagation(); onAcao(acao.acao); }}
                  className={`${primario} hidden sm:inline-block`}>
                  {acao.rotulo}
                </button>
              )}
            </div>
            {aberto === s.id && <CartaoDeServico s={s} ctx={ctx} onAcao={onAcao} embutido />}
          </div>
        );
      })}
      {lista.length > visiveis.length && (
        <button onClick={() => setTodos(true)} className="w-full px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">
          Mostrar todos ({lista.length})
        </button>
      )}
    </div>
  );
};

/** O cartão do cliente: nome, contato, os serviços dele e as ações. */
export const CartaoDeCliente = ({ c, ctx, onAcao }) => {
  const servicos = abertosPrimeiro(servicosDoCliente(ctx, c.id));
  return (
    <div className="space-y-2">
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <p className="font-semibold text-slate-900">{c.name}</p>
        {c.phone && <p className="text-sm text-slate-500">{c.phone}</p>}
        <div className="mt-2 flex flex-wrap gap-x-1">
          {acoesDoCliente(c).map((b, i) => (
            <button key={i} onClick={() => onAcao(b.acao)} className={secundario}>{b.rotulo}</button>
          ))}
        </div>
      </div>
      {servicos.length > 0
        ? <ListaDeServicos ids={servicos.map(s => s.id)} ctx={ctx} onAcao={onAcao} />
        : <p className="text-sm text-slate-500">Nenhum serviço cadastrado.</p>}
    </div>
  );
};

/**
 * As pendências: uma seção por tipo, com a contagem, o valor e "fazer em
 * sequência". Abrem fechadas, como os grupos do Financeiro; aberta, a seção
 * é a lista dos serviços dela.
 */
export const Pendencias = ({ cliente, ctx, onAcao }) => {
  const [aberta, setAberta] = useState(null);
  const grupos = gruposDePendencias(ctx, cliente);
  if (grupos.length === 0) return <p className="text-sm text-slate-500">Nada pendente agora.</p>;
  return (
    <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100 overflow-hidden">
      {grupos.map(g => {
        const fila = filaDoGrupo(g, 'Fazer em sequência');
        return (
          <div key={g.id}>
            <div role="button" tabIndex={0}
              onClick={() => setAberta(aberta === g.id ? null : g.id)}
              onKeyDown={(e) => { if (e.key === 'Enter') setAberta(aberta === g.id ? null : g.id); }}
              className="flex items-center gap-3 px-3 py-3 cursor-pointer hover:bg-slate-50">
              <ChevronRight className={`w-4 h-4 shrink-0 text-slate-400 transition-transform ${aberta === g.id ? 'rotate-90' : ''}`} />
              <p className="flex-1 text-sm text-slate-900">
                <span className="font-medium">{g.titulo}</span>
                <span className="text-slate-500"> · {g.itens.length}{g.valor ? ` · ${reais(g.valor)}` : ''}</span>
              </p>
              <button onClick={(e) => { e.stopPropagation(); onAcao(fila.acao); }} className={primario}>
                {fila.rotulo}
              </button>
            </div>
            {aberta === g.id && (
              <div className="px-3 pb-3">
                <ListaDeServicos ids={g.itens.map(i => i.s.id)}
                  notas={Object.fromEntries(g.itens.map(i => [i.s.id, i.detalhe]))}
                  ctx={ctx} onAcao={onAcao} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/** Desenha o bloco de uma resposta. Item que sumiu da lista não quebra a tela. */
export const BlocoDoAssistente = ({ bloco, ctx, onAcao }) => {
  if (!bloco) return null;
  if (bloco.tipo === 'servicos') {
    return <ListaDeServicos ids={bloco.ids} notas={bloco.notas} acoes={bloco.acoes} ctx={ctx} onAcao={onAcao} />;
  }
  if (bloco.tipo === 'servico') {
    const s = (ctx.services || []).find(x => x.id === bloco.id);
    return s ? <CartaoDeServico s={s} ctx={ctx} onAcao={onAcao} /> : null;
  }
  if (bloco.tipo === 'cliente') {
    const c = (ctx.clients || []).find(x => x.id === bloco.id);
    return c ? <CartaoDeCliente c={c} ctx={ctx} onAcao={onAcao} /> : null;
  }
  if (bloco.tipo === 'pendencias') return <Pendencias cliente={bloco.cliente} ctx={ctx} onAcao={onAcao} />;
  if (bloco.tipo === 'prenhas') return <PrenhasPorCliente grupos={bloco.grupos} />;
  return null;
};
